import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminGetSessions, adminKillSession } from '../../api/admin';
import type { SessionEntry } from '../../types/auth';
import { useCallStore } from '../../store/useCallStore';
import { useToastStore } from '../../store/useToastStore';
import { TH, fmtDate, timeRemaining, parseUA, usePaginated, PaginationBar, ConfirmDialog } from './shared';

interface Props { currentSessionId?: string }

export default function SessionsTab({ currentSessionId }: Props) {
  const addToast = useToastStore((s) => s.addToast);
  const sessionVersion = useCallStore((s) => s.sessionVersion);
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [confirmKill, setConfirmKill] = useState<SessionEntry | null>(null);

  const load = useCallback(async () => {
    try {
      setSessions(await adminGetSessions());
    } catch {
      addToast('Fehler beim Laden der Sitzungen.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  // Refresh immediately when a session_update WS event fires
  useEffect(() => { load(); }, [sessionVersion, load]);
  // Auto-refresh every 30 s as fallback
  useEffect(() => {
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQ.toLowerCase();
    return !q ? sessions : sessions.filter((s) =>
      s.displayName.toLowerCase().includes(q) ||
      (s.companyName ?? '').toLowerCase().includes(q) ||
      s.ipAddress.includes(q),
    );
  }, [sessions, searchQ]);

  const pag = usePaginated(filtered, 25);

  async function doKill(session: SessionEntry) {
    try {
      await adminKillSession(session.sessionId);
      addToast(`Sitzung von ${session.displayName} beendet.`);
      setConfirmKill(null);
      await load();
    } catch {
      addToast('Fehler beim Beenden der Sitzung.', 'danger');
    }
  }

  if (loading) return <div className="text-center py-5"><span className="spinner-border" /></div>;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
        <input
          type="search"
          className="form-control form-control-sm"
          style={{ maxWidth: 280 }}
          placeholder="Suche nach Name, Unternehmen, IP…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
        />
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-primary-subtle text-primary-emphasis" style={{ fontSize: '0.8rem' }}>
            {sessions.length} aktive Sitzung{sessions.length !== 1 ? 'en' : ''}
          </span>
          <button className="btn btn-outline-secondary btn-sm" onClick={load} title="Aktualisieren">↻</button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.83rem' }}>
          <thead style={TH}>
            <tr>
              <th className="ps-3">Benutzer</th>
              <th>Unternehmen</th>
              <th>Angemeldet</th>
              <th>Läuft ab</th>
              <th>Verbleibend</th>
              <th>Browser</th>
              <th>IP-Adresse</th>
              <th className="text-end pe-3">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {pag.slice.map((s) => {
              const { label, color } = timeRemaining(s.expiresAt);
              const isCurrent = s.sessionId === currentSessionId;
              return (
                <tr key={s.sessionId} className={isCurrent ? 'table-primary' : ''}>
                  <td className="ps-3">
                    <div className="fw-semibold">{s.displayName}</div>
                    <div className="d-flex gap-1 mt-1">
                      <span className={`badge ${s.role === 'admin' ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                        {s.role === 'admin' ? 'Admin' : 'Benutzer'}
                      </span>
                      {isCurrent && <span className="badge bg-info-subtle text-info-emphasis" style={{ fontSize: '0.65rem' }}>Diese Sitzung</span>}
                    </div>
                  </td>
                  <td>{s.companyName ?? <span className="text-muted">—</span>}</td>
                  <td className="text-nowrap">{fmtDate(s.issuedAt)}</td>
                  <td className="text-nowrap">{fmtDate(s.expiresAt)}</td>
                  <td><span className="fw-semibold" style={{ color }}>{label}</span></td>
                  <td>{parseUA(s.userAgent)}</td>
                  <td className="font-monospace" style={{ fontSize: '0.78rem' }}>{s.ipAddress || '—'}</td>
                  <td className="text-end pe-3">
                    <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '0.72rem' }} onClick={() => setConfirmKill(s)}>
                      Beenden
                    </button>
                  </td>
                </tr>
              );
            })}
            {pag.slice.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-4">Keine aktiven Sitzungen</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={pag.page} totalPages={pag.totalPages} pageSize={pag.pageSize} total={pag.total} onPage={pag.setPage} onPageSize={pag.setPageSize} />

      {confirmKill && (
        <ConfirmDialog
          title="Sitzung beenden?"
          text={
            <>
              {confirmKill.displayName}
              {confirmKill.sessionId === currentSessionId && (
                <><br /><span className="text-danger">Das ist Ihre eigene Sitzung — Sie werden abgemeldet!</span></>
              )}
            </>
          }
          confirmLabel="Beenden"
          onConfirm={() => doKill(confirmKill)}
          onCancel={() => setConfirmKill(null)}
        />
      )}
    </>
  );
}
